import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Button,
  DialogContentText,
  IconButton,
  Menu,
  MenuItem,
  SxProps,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BulkUploadIcon from "../../src/assets/BulkUploadIcon.svg";
import {
  activeUser,
  deactiveUser,
  getUsers,
  removeUser,
} from "../api/requests/users";
import CommDataGrid from "../components/CommDataGrid";
import CommonDialog from "../components/common/CommonDialog";
import { useAlert } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import { colors } from "../utils/constants";
import BulkInvite from "./BulkInviteUser";
import UserForm from "./createUser";
import { menuItemStyles, PaginationType } from "./Groups";

//Images
import ActiveTickIcon from "../../src/assets/ActiveTickIcon.svg";
import AddIcon from "../../src/assets/AddIcon.svg";
import InctiveCrossIcon from "../../src/assets/InactiveCrossIcon.svg";
import { getAllAssemblyConstituency } from "../api/requests/assemblyConstituency";
import { getAllRIO } from "../api/requests/RIO";
import { getAllZone } from "../api/requests/zones";
import {
  getAssemblyConstituencyNameById,
  getRIONameById,
  getZoneNameById,
} from "../utils/utils";

// Define User and UserMetrics interfaces
interface User {
  id: number;
  name: string;
  mobile: string;
  email: string;
  dateOfBirth: string;
  policeStation: string;
  zone: string;
  location: string;
  rank: string;
  position: string;
  region: string;
  status: string;
  emailConfirmed: boolean;
  assemblyConstituency: string;
}

const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const [selectedRow, setSelectedRow] = useState<User | null>(null);
  const [actionDialog, setActionDialog] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState<PaginationType>({
    page: 0,
    pageSize: 10,
  });
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>("");
  const [openDialog, setOpenDialog] = useState<string>("");
  const closeActionDialog = () => {
    setActionDialog(null);
  };

  const closeUserDialog = (buttonText: string) => {
    setOpenDialog("");
    if (buttonText === "create" || buttonText === "invite") {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    } else if (buttonText === "edit") {
      fetchUserData();
    }
  };

  const openActionDialog = (value: string) => {
    setActionDialog(value);
    handleMenuClose();
  };

  // Fetch data from API
  const fetchUserData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchUsers()]);
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, searchText]);

  const fetchZones = async () => {
    const zoneRes = await getAllZone();
    if (zoneRes.status == 200) {
      return zoneRes.data;
    }
  };
  const fetchAllRIOs = async () => {
    const rioRes = await getAllRIO();
    if (rioRes.status == 200) {
      return rioRes.data;
    }
  };

  const fetchAssemblyConstituencies = async () => {
    const assemblyConstituencyRes = await getAllAssemblyConstituency();
    if (assemblyConstituencyRes.status == 200) {
      return assemblyConstituencyRes.data;
    }
  };

  // Fetch users and metrics
  const fetchUsers = async () => {
    try {
      const response = await getUsers({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        q: searchText.trim(),
      });
      const rioRes = await fetchAllRIOs();
      const zonesRes = await fetchZones();
      const assemblyConstituencyListRes = await fetchAssemblyConstituencies();
      if (response && response.data && response.data.users) {
        const usersWithMappedFields = response.data.users.map((user: any) => ({
          name: user.fullName,
          id: user.userId,
          mobile: user.mobileNo,
          location: user.location || "",
          zone: user.zoneId
            ? getZoneNameById({ zoneList: zonesRes, zoneId: user.zoneId })
            : "",
          status: user.status || "",
          email: user.email,
          rank: user.rankName,
          emailConfirmed: user.emailConfirmed,
          assemblyConstituency: user.assemblyConstituencyId
            ? getAssemblyConstituencyNameById({
                assemblyList: assemblyConstituencyListRes,
                assemblyId: user.assemblyConstituencyId,
              })
            : "",
          rio: user.rioId
            ? getRIONameById({
                rioList: rioRes,
                rioId: user.rioId,
              })
            : "",
          generalNumber: user.generalNumber || "",
          employeeId: user.employeeId,
        }));
        const ids = usersWithMappedFields.map((g: any) => g.id);
        const uniqueIds = new Set(ids);
        if (ids.length !== uniqueIds.size) {
          console.error("Duplicate IDs detected:", ids);
        }
        setUsers(usersWithMappedFields);
        setTotalRowsCount(response.data.totalCount);
      } else {
        setUsers([]);
        setTotalRowsCount(0);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const handleEdit = () => {
    if (selectedRow) {
      handleMenuClose();
      setOpenDialog("edit");
    }
  };

  const viewAnalytics = () => {
    if (selectedRow) {
      handleMenuClose();
      navigate(`useranalytics/${selectedRow.id}`);
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) {
      showAlert("No role selected", "error");
      return;
    }
    const userId = selectedRow.id;
    try {
      const response = await removeUser(userId);
      if (response && response.status === 200) {
        showAlert("User deleted successfully", "success");
        closeActionDialog();
        await fetchUsers();
        setSelectedRow(null);
      }
    } catch (error: any) {
      console.error("Error deleting role:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to delete role";
      showAlert(errorMessage, "error");
      setSelectedRow(null);
    }
    handleMenuClose();
  };

  const handleSearch = useCallback((value: string) => {
    setSearchText(value.trim());
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const createUserHandler = () => {
    setOpenDialog("create");
  };

  const bulkUploadHandler = () => {
    setOpenDialog("bulkUpload");
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const updateStatus = async () => {
    if (!selectedRow) return;
    const { id: userId } = selectedRow;
    const action = selectedRow.status === "Active" ? deactiveUser : activeUser;
    try {
      const response = await action({ userId });
      if (response && response.status === 200) {
        showAlert("Status updated successfully", "success");
        closeActionDialog();
        await fetchUsers();
        setSelectedRow(null);
      } else {
        showAlert("Status updation failed", "error");
      }
    } catch (error) {
      console.error("Error updating user status:", error);
    }
  };

  // Memoize rows to stabilize prop
  const memoizedRows = useMemo(() => users, [users]);

  // Fetch the data when the component mounts
  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Columns for the DataGrid
  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ display: "contents" }}>
          {params.value || "--"}
        </Typography>
      ),
    },
    {
      field: "employeeId",
      headerName: "EmployeeId",
      flex: 1,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ display: "contents" }}>
          {params.value || "--"}
        </Typography>
      ),
    },
    {
      field: "rank",
      headerName: "Rank",
      flex: 0.7,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ display: "contents" }}>
          {params.value || "--"}
        </Typography>
      ),
    },
    // {
    //   field: 'emailConfirmed',
    //   headerName: 'Email confirmed',
    //   flex: 1,
    //   headerClassName: 'custom-header',
    //   disableColumnMenu: true,
    //   renderCell: (params) => {
    //     return (
    //       <Typography
    //         component='span'
    //         style={{
    //           backgroundColor: params.value === true ? '#e0f7fa' : '#ffebee',
    //           color: params.value === true ? '#006064' : '#d32f2f',
    //           padding: '5px 10px',
    //           borderRadius: '15px',
    //           fontSize: '12px',
    //         }}
    //       >
    //         {params.value === true ? 'Yes' : 'No'}
    //       </Typography>
    //     );
    //   },
    // },
    {
      field: "location",
      headerName: "Location",
      flex: 1,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ display: "contents" }}>
          {params.value || "--"}
        </Typography>
      ),
    },
    {
      field: "mobile",
      headerName: "Mobile",
      flex: 0.7,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ display: "contents" }}>
          {params.value || "--"}
        </Typography>
      ),
    },
    {
      field: "rio",
      headerName: "RIO",
      flex: 1,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ display: "contents" }}>
          {params.value || "--"}
        </Typography>
      ),
    },
    {
      field: "zone",
      headerName: "Zone",
      flex: 0.8,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ display: "contents" }}>
          {params.value || "--"}
        </Typography>
      ),
    },
    {
      field: "assemblyConstituency",
      headerName: "Assembly Constituency",
      flex: 1,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ display: "contents" }}>
          {params.value || "--"}
        </Typography>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ display: "contents" }}>
          {params.value || "--"}
        </Typography>
      ),
    },
    {
      field: "generalNumber",
      headerName: "General Number",
      flex: 1,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ display: "contents" }}>
          {params.value || "--"}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.6,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
            borderRadius: "15px",
          }}
        >
          <img
            src={params.value === "Active" ? ActiveTickIcon : InctiveCrossIcon}
            alt="status-icon"
            style={{ width: 20, height: 20, marginRight: 8 }}
          />
          <Typography
            component="span"
            sx={{
              fontSize: "14px",
            }}
          >
            {params.value || ""}
          </Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      sortable: false,
      disableColumnMenu: true,
      headerClassName: "custom-header",
      renderCell: (params) => (
        <>
          {params.row.status !== "Deleted" && (
            <>
              <IconButton
                onClick={(event) => {
                  handleMenuOpen(event, params.row);
                }}
              >
                <MoreVertIcon sx={{ color: "#64748B" }} />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                sx={menuItemStyles}
              >
                {user?.permissions && (
                  <>
                    {user.permissions.some(
                      (p) => p.module === "Users" && p.actions.includes("Edit")
                    ) ? (
                      <MenuItem onClick={handleEdit}>Edit</MenuItem>
                    ) : null}
                  </>
                )}{" "}
                {user?.permissions && (
                  <>
                    {user.permissions.some(
                      (p) => p.module === "Users" && p.actions.includes("Edit")
                    ) ? (
                      <MenuItem
                        onClick={() => openActionDialog(params.row.status)}
                      >
                        {selectedRow && selectedRow.status === "Active"
                          ? "Inactive"
                          : "Active"}
                      </MenuItem>
                    ) : null}
                  </>
                )}{" "}
                {user?.permissions && (
                  <>
                    {user.permissions.some(
                      (p) =>
                        p.module === "Users" && p.actions.includes("Delete")
                    ) ? (
                      <MenuItem
                        onClick={() =>
                          selectedRow && openActionDialog("Delete")
                        }
                      >
                        Delete
                      </MenuItem>
                    ) : null}
                  </>
                )}{" "}
                <MenuItem onClick={viewAnalytics}>View Analytics</MenuItem>
              </Menu>
            </>
          )}
        </>
      ),
    },
  ];

  const buttonsComponent = useCallback(
    () => (
      <>
        {" "}
        {user?.permissions && (
          <>
            {user.permissions.some(
              (p) => p.module === "Users" && p.actions.includes("Create")
            ) ? (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Bulk Invite" placement="top">
                  <Button
                    variant="outlined"
                    onClick={bulkUploadHandler}
                    sx={bulkUploadStyles}
                  >
                    <img src={BulkUploadIcon} />
                  </Button>
                </Tooltip>
                <Button
                  variant="contained"
                  onClick={createUserHandler}
                  sx={containedButtonWithIconSx}
                >
                  <img src={AddIcon} /> Add User
                </Button>
              </Box>
            ) : null}
          </>
        )}{" "}
      </>
    ),
    [user]
  );

  return (
    <Box>
      <CommDataGrid
        title="User Management"
        description="View and manage all users in the system"
        rows={memoizedRows}
        columns={columns}
        section="Users"
        renderButtons={buttonsComponent}
        setPaginationModel={setPaginationModel}
        paginationModel={paginationModel}
        totalRowsCount={totalRowsCount}
        searchText={searchText}
        handleSearch={handleSearch}
        loading={loading}
      />
      {!!openDialog && (
        <CommonDialog
          hideActionButtons={true}
          title={
            openDialog === "bulkUpload"
              ? "File Upload"
              : openDialog === "create"
              ? "Create New User"
              : "Edit User"
          }
          minWidth={720}
          cancelButton={() => setOpenDialog("")}
        >
          <DialogContentText>
            {openDialog === "bulkUpload" ? (
              <BulkInvite closeDialog={closeUserDialog} />
            ) : (
              <UserForm
                mode={openDialog}
                closeUserDialog={closeUserDialog}
                userId={selectedRow?.id || null}
              />
            )}
          </DialogContentText>
        </CommonDialog>
      )}
      {!!actionDialog && (
        <CommonDialog
          title={
            actionDialog === "Delete"
              ? "Delete User"
              : selectedRow?.status === "Active"
              ? "Deactivate User"
              : "Activate User"
          }
          cancelButton={closeActionDialog}
          actionButton={
            actionDialog === "Delete"
              ? () => {
                  handleDelete();
                }
              : () => {
                  updateStatus();
                }
          }
          actionButtonTitle="Yes"
          cancelButtonTitle="cancel"
        >
          <DialogContentText>
            <Typography
              fontSize="13px"
              sx={{ color: "#000000", paddingBottom: 1, pl: 2 }}
            >
              {`Are you sure you want to ${
                actionDialog === "Delete"
                  ? "delete"
                  : selectedRow?.status === "Active"
                  ? "deactivate"
                  : "activate"
              } the user?`}
            </Typography>
            <Typography fontSize="12px" pl={2}>
              {`The user will be ${
                actionDialog === "Delete"
                  ? "deleted"
                  : selectedRow?.status === "Active"
                  ? "deactivated"
                  : "activated"
              } if you click on 'Yes'.`}
            </Typography>
          </DialogContentText>
        </CommonDialog>
      )}
    </Box>
  );
};

export default UserManagementPage;

export const containedButtonWithIconSx: SxProps = {
  display: "flex",
  gap: 1,
  borderRadius: "8px",
  backgroundColor: colors.indigoBlueColor,
  color: colors.white,
  textTransform: "inherit",
  ml: 1,
  whiteSpace: "nowrap",
};

const bulkUploadStyles: SxProps = {
  display: "flex",
  gap: 1,
  borderRadius: "8px",
  backgroundColor: colors.white,
};
